// src/components/admin/analytics/PerformanceTab.jsx
import { FaServer, FaExternalLinkAlt, FaFire, FaDatabase, FaBolt, FaExclamationTriangle } from 'react-icons/fa';
import { MdRefresh } from 'react-icons/md';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import usePerformanceAnalytics from '../../../hooks/usePerformanceAnalytics';

const fmt = (val) => {
  if (val === null || val === undefined) return '—';
  if (val >= 1_000_000) return `${(val / 1_000_000).toFixed(1)}M`;
  if (val >= 1_000) return `${(val / 1_000).toFixed(1)}K`;
  return val.toLocaleString();
};

const MetricCard = ({ title, value, sub, icon: Icon, color, bgColor, warn }) => (
  <div className={`bg-white rounded-lg shadow p-6 ${warn ? 'ring-2 ring-red-200' : ''}`}>
    <div className="flex items-center justify-between mb-4">
      <div className={`${bgColor} p-3 rounded-full`}>
        <Icon className={`w-6 h-6 ${color}`} />
      </div>
      {warn && <FaExclamationTriangle className="w-4 h-4 text-red-400" />}
    </div>
    <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
    <p className="text-2xl font-bold text-gray-900">{value}</p>
    {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
  </div>
);

const SkeletonCard = () => (
  <div className="bg-white rounded-lg shadow p-6 animate-pulse">
    <div className="w-12 h-12 bg-gray-200 rounded-full mb-4" />
    <div className="h-3 bg-gray-200 rounded w-3/4 mb-2" />
    <div className="h-7 bg-gray-200 rounded w-1/2" />
  </div>
);

const QuickLink = ({ href, label, sub }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="flex items-center justify-between p-4 bg-white rounded-lg shadow hover:shadow-md transition-shadow group"
  >
    <div>
      <p className="text-sm font-semibold text-gray-800 group-hover:text-green-700 transition-colors">{label}</p>
      <p className="text-xs text-gray-500 mt-0.5">{sub}</p>
    </div>
    <FaExternalLinkAlt className="w-3.5 h-3.5 text-gray-400 group-hover:text-green-600 transition-colors" />
  </a>
);

const MiniChart = ({ data, dataKey, color }) => {
  if (!data?.length) return <p className="text-xs text-gray-400 text-center py-4">No daily data</p>;
  return (
    <ResponsiveContainer width="100%" height={80}>
      <AreaChart data={data} margin={{ top: 4, right: 4, left: -30, bottom: 0 }}>
        <defs>
          <linearGradient id={`grad-${dataKey}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={color} stopOpacity={0.2} />
            <stop offset="95%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
        <XAxis dataKey="time" tick={{ fontSize: 9, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
        <YAxis tick={{ fontSize: 9, fill: '#9ca3af' }} axisLine={false} tickLine={false} tickFormatter={fmt} />
        <Tooltip
          formatter={(v) => [fmt(v), dataKey]}
          contentStyle={{ fontSize: '11px', borderRadius: '6px', border: '1px solid #e5e7eb' }}
        />
        <Area type="monotone" dataKey="value" stroke={color} strokeWidth={2} fill={`url(#grad-${dataKey})`} dot={false} />
      </AreaChart>
    </ResponsiveContainer>
  );
};

const PerformanceTab = () => {
  const { data, loading, error, refresh } = usePerformanceAnalytics();

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => <SkeletonCard key={i} />)}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-white rounded-lg shadow p-6 animate-pulse h-48" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-5 flex items-center justify-between">
          <p className="text-sm text-red-700">{error}</p>
          <button
            onClick={refresh}
            className="flex items-center gap-2 px-3 py-1.5 bg-red-100 text-red-700 text-sm rounded-lg hover:bg-red-200"
          >
            <MdRefresh className="w-4 h-4" /> Retry
          </button>
        </div>
        <GCPLinks />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">Last 30 days · pulled from GCP Cloud Monitoring</p>
        <button
          onClick={refresh}
          className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 shadow-sm"
        >
          <MdRefresh className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Hosting Requests"
          value={fmt(data?.hosting?.requests)}
          sub="30-day total"
          icon={FaServer}
          color="text-blue-600"
          bgColor="bg-blue-100"
        />
        <MetricCard
          title="Bandwidth Used"
          value={data?.hosting?.bandwidth || '—'}
          sub="data served to users"
          icon={FaBolt}
          color="text-purple-600"
          bgColor="bg-purple-100"
        />
        <MetricCard
          title="Function Invocations"
          value={fmt(data?.functions?.invocations)}
          sub={`${data?.functions?.errorRate ?? '—'}% error rate`}
          icon={FaFire}
          color="text-orange-600"
          bgColor="bg-orange-100"
          warn={data?.functions?.errorRate > 5}
        />
        <MetricCard
          title="Firestore Operations"
          value={fmt(data?.firestore?.total)}
          sub={`${fmt(data?.firestore?.reads)} reads · ${fmt(data?.firestore?.writes)} writes`}
          icon={FaDatabase}
          color="text-green-600"
          bgColor="bg-green-100"
        />
      </div>

      {/* Sparkline Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-base font-semibold text-gray-900 mb-1">Hosting Requests — Last 7 Days</h3>
          <MiniChart data={data?.hosting?.requestsDaily} dataKey="requests" color="#3b82f6" />
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-base font-semibold text-gray-900 mb-1">Function Invocations — Last 7 Days</h3>
          <MiniChart data={data?.functions?.invocationsDaily} dataKey="invocations" color="#f97316" />
        </div>
      </div>

      {/* Firestore Breakdown */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-base font-semibold text-gray-900 mb-4">Firestore Breakdown (30d)</h3>
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: 'Reads', value: data?.firestore?.reads, color: 'bg-green-100 text-green-700' },
            { label: 'Writes', value: data?.firestore?.writes, color: 'bg-blue-100 text-blue-700' },
            { label: 'Deletes', value: data?.firestore?.deletes, color: 'bg-red-100 text-red-700' },
          ].map(({ label, value, color }) => (
            <div key={label} className={`${color} rounded-lg p-4 text-center`}>
              <p className="text-2xl font-bold">{fmt(value)}</p>
              <p className="text-sm font-medium mt-1">{label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Links */}
      <GCPLinks />
    </div>
  );
};

const GCPLinks = () => (
  <div>
    <h3 className="text-base font-semibold text-gray-900 mb-3">GCP Quick Links</h3>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <QuickLink
        href="https://console.firebase.google.com/project/winnepocket-landscaping/hosting"
        label="Firebase Hosting"
        sub="Requests, bandwidth, deploy history"
      />
      <QuickLink
        href="https://console.firebase.google.com/project/winnepocket-landscaping/functions"
        label="Cloud Functions"
        sub="Invocations, errors, execution times"
      />
      <QuickLink
        href="https://console.firebase.google.com/project/winnepocket-landscaping/firestore/usage"
        label="Firestore Usage"
        sub="Read, write, delete counts"
      />
      <QuickLink
        href="https://console.cloud.google.com/monitoring/dashboards?project=winnepocket-landscaping"
        label="GCP Cloud Monitoring"
        sub="Custom dashboards and alerting"
      />
    </div>
  </div>
);

export default PerformanceTab;